import { noteOnboardingNavigation } from "@/lib/analytics/onboarding-funnel";
import { findActiveTab } from "@/lib/host-panel/host-tabs";

/**
 * The bridge between pages: the porcelain veil with the monogram (`nav-transition.js` in the
 * final design package, MVP-NOVA-LISTA). Leaving covers, arriving reveals.
 *
 * Phases, as the prototype runs them:
 * - `arrival`   — the first frame of a full load is already covered; revealed once the page has
 *                 hydrated and its fonts settled.
 * - `covering`  — a navigation left this page: the veil comes in over it, and stays until the
 *                 next page has committed *and* the veil has finished coming in.
 * - `revealing` — the veil leaves the page that arrived.
 * - `idle`      — nothing on screen.
 *
 * The start of a navigation comes from `onRouterTransitionStart` (`instrumentation-client.ts`),
 * the App Router's only global signal for it. Next calls it only for a navigation actually
 * dispatched: a `<Link>` whose own `onClick` cancelled it (the help links that open a dialog)
 * never reaches it, while `router.push`, back/forward and the panel's deferred tab navigation all
 * do. A document-level click listener could not tell those apart.
 */

/**
 * The veil coming in: the prototype's ENTRA, 200ms (`nav-transition.js`). Off the duration scale
 * on purpose: a number written in the design is the number on screen (canon §11).
 */
export const VEIL_COVER_MS = 200;

/** The veil leaving: the prototype's SAI, 380ms (`nav-transition.js`). Off the scale, likewise. */
export const VEIL_REVEAL_MS = 380;

/** Longest wait for web fonts before revealing anyway (prototype: 900ms). */
export const VEIL_FONT_WAIT_MS = 900;

/**
 * Ceiling for the arrival (prototype `TETO`: "nunca deixar o véu preso"). The stylesheet enforces
 * the same figure on its own, so a page whose script never runs is still revealed.
 */
export const VEIL_ARRIVAL_CEILING_MS = 2600;

/**
 * Ceiling for a client navigation that never commits. Not in the prototype, which navigated
 * between static files and could not stall; here a navigation can be superseded or dropped, and
 * the veil must not outlive it.
 *
 * It is a net for a navigation that died, never a wait for a slow page: the veil must always
 * bring the next page and never show again the one it covered, failures aside (the person's rule
 * of 2026-10-06). So it sits well above what a page may take to answer — one backend read can
 * wait 10s (`server-http.ts`) — where the first ceiling, at exactly 10s, revealed the page left
 * behind while the next one was still on its way.
 */
export const VEIL_STALL_CEILING_MS = 30_000;

/**
 * Ceiling for leaving by a full document load. The browser replaces the page itself and shows
 * its own error page when the load fails, so the veil only gives up on a load the person stopped.
 */
export const VEIL_DOCUMENT_STALL_CEILING_MS = 60_000;

/** How a navigation moves through history, as the App Router names it (`onRouterTransitionStart`). */
export type PageNavigationType = "push" | "replace" | "traverse";

/** Who carries the navigation: the App Router, or the browser loading a whole new document. */
export type PageNavigationCarrier = "router" | "document";

export type PageVeilPhase = "arrival" | "covering" | "revealing" | "idle";

export interface PageVeil {
  phase: PageVeilPhase;
  /** Pathname a `covering` navigation left from; null otherwise. */
  from: string | null;
  /** Who carries a `covering` navigation; it decides how long the veil may wait for it. */
  carrier: PageNavigationCarrier;
  /** `performance.now()` when the current cover began. */
  startedAt: number;
}

const ARRIVAL: PageVeil = { phase: "arrival", from: null, carrier: "router", startedAt: 0 };

let current: PageVeil = ARRIVAL;

/**
 * Whether the page on screen was reached by going back or forward in history. Every other arrival
 * starts at the top of the page; this one returns to where the person left it.
 *
 * A full load reports it through the Navigation Timing entry (`back_forward`); a client
 * navigation through `onRouterTransitionStart`. The page loader is not mounted everywhere
 * (checkout, admin), so the type is recorded before any of its decisions.
 */
let lastNavigationType: PageNavigationType = initialNavigationType();

function initialNavigationType(): PageNavigationType {
  if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") return "push";
  const [entry] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];

  return entry?.type === "back_forward" ? "traverse" : "push";
}

/** The page on screen came back through history: its scroll position is restored, not reset. */
export function isHistoryTraversal(): boolean {
  return lastNavigationType === "traverse";
}

/**
 * Puts the page at its top at once, under whichever engine drives the scroll: the shared smooth
 * scroll (`window.__tbLenis`) would otherwise slide back to where it was, so it is told first;
 * the window follows for every page that has none. `behavior: "instant"` overrides a
 * stylesheet's `scroll-behavior: smooth`.
 */
export function scrollPageToTop(): void {
  if (typeof window === "undefined") return;

  window.__tbLenis?.scrollTo(0, { immediate: true, force: true });
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
}
/**
 * The pathname on screen, as the page loader last rendered it. On back/forward the browser has
 * already moved `location` by the time the router announces the traversal, so `location` would
 * name the destination as the origin.
 */
let renderedPathname: string | null = null;
let idleTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function set(next: PageVeil): void {
  current = next;
  for (const listener of listeners) listener();
}

/**
 * Switching tabs inside the host panel is not a page transition: the panel has its own approved
 * answer for it (the tab exits, the loading header names the tab arriving).
 */
function isPanelTabSwitch(from: string, to: string): boolean {
  return findActiveTab(from) !== undefined && findActiveTab(to) !== undefined;
}

/**
 * Covers the page being left.
 *
 * Only a change of pathname counts. A query or hash change on the same page (search filters,
 * section anchors) is content arriving, which is the skeletons' and the page's own business.
 */
export function announcePageTransition(
  href: string,
  navigationType: PageNavigationType = "push",
  carrier: PageNavigationCarrier = "router",
): void {
  if (typeof window === "undefined") return;

  lastNavigationType = navigationType;

  let target: URL;
  try {
    target = new URL(href, window.location.href);
  } catch {
    return;
  }

  const from = renderedPathname ?? window.location.pathname;
  if (target.pathname === from || isPanelTabSwitch(from, target.pathname)) return;

  clearTimeout(idleTimer);
  set({ phase: "covering", from, carrier, startedAt: performance.now() });
}

/**
 * Leaves by a full document load the script starts itself — after a sign-in, so the new session
 * cookie rides along (`wizard-pattern.md`). The router never hears of it and no anchor was
 * clicked, so the veil is called here first: the page is covered while the next one loads, and
 * the next document arrives already covered.
 */
export function leaveForDocument(href: string): void {
  // The document is about to end by the flow's own doing, not by the person leaving it.
  noteOnboardingNavigation();
  announcePageTransition(href, "push", "document");
  window.location.href = href;
}

/** Lets the veil leave, then clears it once it has. */
export function revealPageVeil(): void {
  if (current.phase === "revealing" || current.phase === "idle") return;

  set({ phase: "revealing", from: null, carrier: current.carrier, startedAt: current.startedAt });
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    if (current.phase === "revealing") set({ phase: "idle", from: null, carrier: "router", startedAt: 0 });
  }, VEIL_REVEAL_MS + 40);
}

/**
 * Called by the page loader with the pathname it is rendering, and with `null` when it unmounts
 * (checkout and admin do not mount it), so a stale pathname never stands in for `location`.
 */
export function notePageVeilPathname(pathname: string | null): void {
  renderedPathname = pathname;
}

export function subscribePageVeil(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getPageVeil(): PageVeil {
  return current;
}

/** The server always renders the arrival: the first frame of a full load is covered. */
export function getServerPageVeil(): PageVeil {
  return ARRIVAL;
}

/** Test seam: back to the state of a fresh load. */
export function resetPageVeilForTests(): void {
  clearTimeout(idleTimer);
  current = ARRIVAL;
  renderedPathname = null;
  lastNavigationType = "push";
}
